import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { preloadable, preloadWhenIdle } from "./utils/preloadable";
import { useAuth } from "./contexts/AuthContext";
import { AuthProvider } from "./contexts/AuthContext";
import { AnalyticsAuthSync } from "./analytics";
import ParentSignupGuard from "./components/ParentSignupGuard";
import { LessonProvider } from "./contexts/LessonContext";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ToastProvider } from "./hooks/useToast";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { IncomingCallBanner } from "./components/IncomingCallBanner";
import { ProposalBanner } from "./components/ProposalBanner";
import { UpdateAvailableToast } from "./components/UpdateAvailableToast";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { useVisualViewportVars } from "./hooks/useVisualViewportVars";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute, { StaffRedirect } from "./components/AdminRoute";
import RootRoute from "./components/RootRoute";
const PlayerCloud = lazy(() => import("./pages/PlayerCloud"));
const MonthlyCoachingMonth = preloadable(() => import("./pages/MonthlyCoachingMonth"));
const MonthlyCoachingBillingPage = preloadable(() => import("./pages/MonthlyCoachingBillingPage"));
const LiveLessonPrepPage = preloadable(() => import("./pages/LiveLessonPrepPage"));
const LivePrepView = preloadable(() => import("./pages/LivePrepView"));
import LoadingSpinner from "./components/LoadingSpinner";

// User-facing routes — lazy-loaded so they're excluded from the initial bundle
const StaffInviteSignup = lazy(() => import("./pages/StaffInviteSignup"));
// const Landing = lazy(() => import("./pages/Landing"));
const VideoStreaming = lazy(() => import("./pages/VideoStreaming"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const CoachRegistrationStep1 = lazy(() => import("./pages/CoachRegistrationStep1"));
const CoachRegistrationStep2 = lazy(() => import("./pages/CoachRegistrationStep2"));
const CoachRegistrationStep3 = lazy(() => import("./pages/CoachRegistrationStep3"));
const CoachSetPassword = lazy(() => import("./pages/CoachSetPassword"));
const CoachCommitment = lazy(() => import("./pages/CoachCommitment"));
const CoachRegistrationSuccess = lazy(() => import("./pages/CoachRegistrationSuccess"));
const PlayerOnboardingStep1 = lazy(() => import("./pages/PlayerOnboardingStep1"));
const PlayerOnboardingUnderThirteenIntercept = lazy(() => import("./pages/PlayerOnboardingUnderThirteenIntercept"));
const PlayerOnboardingParentChildTennis = lazy(() => import("./pages/PlayerOnboardingParentChildTennis"));
const ParentSignupRecovery = lazy(() => import("./pages/ParentSignupRecovery"));
const PlayerOnboardingStep2 = lazy(() => import("./pages/PlayerOnboardingStep2"));
const PlayerOnboardingStep3 = lazy(() => import("./pages/PlayerOnboardingStep3"));
const CoachProfilePage = lazy(() => import("./pages/CoachProfilePage"));
const PublicCoachProfilePage = lazy(() => import("./pages/PublicCoachProfilePage"));
const MessageCoachPage = lazy(() => import("./pages/MessageCoachPage"));
const PlayerProfilePage = lazy(() => import("./pages/PlayerProfilePage"));
const CoachProfileSetup = lazy(() => import("./pages/CoachProfileSetup"));
const Feed = lazy(() => import("./pages/Feed"));
const CreateReel = lazy(() => import("./pages/CreateReel"));
const Discover = lazy(() => import("./pages/Discover"));
const Inbox = preloadable(() => import("./pages/Inbox"));
const Orders = preloadable(() => import("./pages/Orders"));
const Lessons = preloadable(() => import("./pages/Lessons"));
const RequestLesson = preloadable(() => import("./pages/RequestLesson"));
const OrderConfirmed = lazy(() => import("./pages/OrderConfirmed"));
const ProgressReportPage = lazy(() => import("./pages/ProgressReportPage"));
const VideoEditorTest = lazy(() => import("./pages/VideoEditorTest"));
const VideoDraftTest = lazy(() => import("./pages/admin/VideoDraftTest"));
const CreateLesson = lazy(() => import("./pages/CreateLesson"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const CollectContactEmail = lazy(() => import("./pages/CollectContactEmail"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const CoachTerms = lazy(() => import("./pages/CoachTerms"));
const CoachStandards = lazy(() => import("./pages/CoachStandards"));
const InviteSignup = lazy(() => import("./pages/InviteSignup"));
const StripeConnectReturn = lazy(() => import("./pages/StripeConnectReturn"));
const MyProfile = preloadable(() => import("./pages/MyProfile"));
const Earnings = lazy(() => import("./pages/Earnings"));
const AccountPage = preloadable(() => import("./pages/AccountPage"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const LiveCallRoom = lazy(() => import("./pages/LiveCallRoom"));
const LiveRoomPage = lazy(() => import("./pages/LiveRoomPage"));
const LiveCallInviteRoom = lazy(() => import("./pages/LiveCallInviteRoom"));

// Admin pages
import { PendingUploadsProvider } from './contexts/PendingUploadsContext';
import { DevAuthProvider, DevErrorBoundary } from './components/DevAuthProvider';
const DevPreview = lazy(() => import('./pages/DevPreview'));
const DevVideoEditorCatalog = lazy(() => import('./pages/DevVideoEditorCatalog'));
const DevVideoEditorCapture = lazy(() => import('./pages/DevVideoEditorCapture'));
const DevVideoEditorScreenshots = lazy(() => import('./pages/DevVideoEditorScreenshots'));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminLessons = lazy(() => import("./pages/admin/Lessons"));
const AdminChats = lazy(() => import("./pages/admin/Chats"));
const AdminMetrics = lazy(() => import("./pages/admin/Metrics"));
const AdminClipLibrary = lazy(() => import("./pages/admin/ClipLibrary"));
const AdminProLibrary = lazy(() => import("./pages/admin/ProLibrary"));
const AdminInvitations = lazy(() => import("./pages/admin/Invitations"));
const AdminBilling = lazy(() => import("./pages/admin/Billing"));
const AdminReconciliation = lazy(() => import("./pages/admin/Reconciliation"));
const AdminLiveLessons = lazy(() => import('./pages/admin/LiveLessons'));
const AdminStaff = lazy(() => import("./pages/admin/Staff"));
const AdminAccount = lazy(() => import("./pages/admin/Account"));
const AdminActionLog = lazy(() => import("./pages/admin/ActionLog"));
const AdminSupport = lazy(() => import("./pages/admin/Support"));

const PageFallback = <LoadingSpinner size="lg" fullScreen />;


/** /lessons/live/:id, the old delivered page's address, opens the lesson's one page. */
/** A member's plan opens in Orders' pane (Ryan, 29 Oct): the old address, kept for links, goes there. */
function MemberPlan() {
  const { membershipId } = useParams();
  return <Navigate to={`/orders?plan=${membershipId}`} replace />;
}

function LegacyLiveLesson() {
  const { bookingId } = useParams();
  return <Navigate to={`/live/${bookingId}`} replace />;
}

/**
 * The lesson screens' code, loaded while the browser is idle once someone is
 * signed in (speed.md 1, 3, decision 157): a tap on Lessons, a lesson, a plan,
 * Messages, Billing or a booking draws its skeleton in the frame it opens,
 * never the full-screen route spinner.
 */
function PreloadLessonScreens() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  useEffect(() => (uid ? preloadWhenIdle([Lessons.preload, Orders.preload, LiveLessonPrepPage.preload, MonthlyCoachingMonth.preload,
    Inbox.preload, RequestLesson.preload, AccountPage.preload, MonthlyCoachingBillingPage.preload, MyProfile.preload]) : undefined), [uid]);
  return null;
}

export default function App() {
  useVersionCheck();
  useVisualViewportVars();

  // Dev routes bypass ALL providers (AuthProvider, LessonProvider, etc.)
  // so they work even when Firebase is offline.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/dev/video-editor')) {
    return (
      <ToastProvider>
        <ResponsiveProvider>
          <Router>
            <DevErrorBoundary>
              <DevAuthProvider>
                <Routes>
                  <Route path="/dev/video-editor-catalog" element={<Suspense fallback={<div />}><DevVideoEditorCatalog /></Suspense>} />
                  <Route path="/dev/video-editor-capture/:stateId" element={<Suspense fallback={<div />}><DevVideoEditorCapture /></Suspense>} />
                  <Route path="/dev/video-editor-screenshots" element={<Suspense fallback={<div />}><DevVideoEditorScreenshots /></Suspense>} />
                </Routes>
              </DevAuthProvider>
            </DevErrorBoundary>
          </Router>
        </ResponsiveProvider>
      </ToastProvider>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ResponsiveProvider>
          <Router>
            <AuthProvider>
              {/* COPPA: opt out PostHog capture for parent-type accounts. */}
              <AnalyticsAuthSync />
              <PreloadLessonScreens />
              <UpdateAvailableToast />
              {/* COPPA: catch orphaned parent accounts (Auth user exists but
                  parentalConsentVerified !== true and no active signup
                  session); sign out + route to recovery screen. */}
              <ParentSignupGuard />
              <LessonProvider>
              <NotificationsProvider>
              <PendingUploadsProvider>
              <IncomingCallBanner />
              <ProposalBanner />
              <Suspense fallback={PageFallback}>
              <Routes>
          {/* Public routes */}
          {/* <Route path="/" element={<StaffRedirect><Landing /></StaffRedirect>} /> */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/dev-preview" element={<DevPreview />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/invite/:token" element={<InviteSignup />} />
          <Route path="/staff-invite/:token" element={<StaffInviteSignup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/collect-contact-email" element={<CollectContactEmail />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/coach-terms" element={<CoachTerms />} />
          <Route path="/coach/standards" element={<CoachStandards />} />
          <Route path="/onboarding/coach/step-1" element={<CoachRegistrationStep1 />} />
          <Route path="/onboarding/coach/step-2" element={<CoachRegistrationStep2 />} />
          <Route path="/onboarding/coach/step-3" element={<CoachRegistrationStep3 />} />
          <Route path="/onboarding/coach/commitment" element={<CoachCommitment />} />
          <Route path="/onboarding/coach/set-password" element={<CoachSetPassword />} />
          <Route path="/onboarding/coach/success" element={<CoachRegistrationSuccess />} />
          <Route path="/onboarding/player/step-1" element={<PlayerOnboardingStep1 />} />
          <Route path="/onboarding/player/under-thirteen-intercept" element={<PlayerOnboardingUnderThirteenIntercept />} />
          <Route path="/onboarding/player/step-2" element={<PlayerOnboardingStep2 />} />
          <Route path="/onboarding/player/parent-child-tennis" element={<PlayerOnboardingParentChildTennis />} />
          <Route path="/onboarding/player/step-3" element={<PlayerOnboardingStep3 />} />
          <Route path="/parent-signup-recovery" element={<ParentSignupRecovery />} />

          {/* Protected routes */}
          <Route
            path="/my-cloud"
            element={
              <PrivateRoute>
                <PlayerCloud />
              </PrivateRoute>
            }
          />
          <Route
            path="/videos"
            element={
              <PrivateRoute>
                <VideoStreaming />
              </PrivateRoute>
            }
          />
          <Route path="/manage-account" element={<Navigate to="/account" replace />} />
          {/* One Billing (audit 2 Nov, A18): Profile's tab. The old address goes there. */}
          <Route path="/billing" element={<Navigate to="/account?tab=billing" replace />} />
          <Route path="/stripe/connect/return" element={<StripeConnectReturn />} />
          <Route path="/stripe/connect/refresh" element={<StripeConnectReturn />} />
          <Route path="/profile" element={<PrivateRoute><MyProfile /></PrivateRoute>} />
          <Route path="/profile/edit" element={<Navigate to="/account?tab=profile" replace />} />
          <Route path="/earnings" element={<PrivateRoute><Earnings /></PrivateRoute>} />
          {/* The old settings page set bookings and offerings a second time; each now has one home on Account. */}
          <Route path="/settings" element={<Navigate to="/account" replace />} />
          <Route path="/account" element={<PrivateRoute><AccountPage /></PrivateRoute>} />
          <Route path="/coach-profile-setup" element={<PrivateRoute><CoachProfileSetup /></PrivateRoute>} />
          <Route path="/feed" element={<PrivateRoute><Feed /></PrivateRoute>} />
          <Route path="/create-reel" element={<PrivateRoute><CreateReel /></PrivateRoute>} />
          <Route path="/discover" element={<StaffRedirect><Discover /></StaffRedirect>} />
          <Route path="/r/:slug" element={<ReferralLanding />} />
          <Route path="/coach/:userId" element={<CoachProfilePage />} />
          <Route path="/c/:slug" element={<PublicCoachProfilePage />} />
          <Route path="/player/:userId" element={<PrivateRoute><PlayerProfilePage /></PrivateRoute>} />
          <Route path="/message/:coachId" element={<PrivateRoute><MessageCoachPage /></PrivateRoute>} />
          <Route path="/inbox" element={<PrivateRoute><Inbox /></PrivateRoute>} />
          <Route path="/inbox/:conversationId" element={<PrivateRoute><Inbox /></PrivateRoute>} />
          <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
          <Route path="/lessons" element={<PrivateRoute><Lessons /></PrivateRoute>} />
          <Route path="/lessons/:conversationId" element={<PrivateRoute><Lessons /></PrivateRoute>} />
          <Route path="/progress-report/:orderId" element={<PrivateRoute><ProgressReportPage /></PrivateRoute>} />
          <Route path="/request-lesson/:coachId/:offeringId?" element={<PrivateRoute><RequestLesson /></PrivateRoute>} />
          <Route path="/monthly-coaching/:membershipId/month" element={<PrivateRoute><MonthlyCoachingMonth /></PrivateRoute>} />
          <Route path="/members/:membershipId" element={<PrivateRoute><MemberPlan /></PrivateRoute>} />
          {/* Under /billing, so the Profile tab is lit: Billing is the account's (Ryan, 28 Oct). */}
          <Route path="/billing/monthly-coaching/:membershipId" element={<PrivateRoute><MonthlyCoachingBillingPage /></PrivateRoute>} />
          {/* The old address of a delivered lesson: every live lesson is /live/:id now. */}
          <Route path="/lessons/live/:bookingId" element={<LegacyLiveLesson />} />
          <Route path="/live/:bookingId" element={<PrivateRoute><LiveLessonPrepPage /></PrivateRoute>} />
          {/* The coach's Prep (161): the match large with the blue Flag and the Moments panel. */}
          <Route path="/live/:bookingId/prep" element={<PrivateRoute><LivePrepView /></PrivateRoute>} />
          <Route path="/order-confirmed/:lessonId" element={<PrivateRoute><OrderConfirmed /></PrivateRoute>} />
          {/* Live call room — accessible without auth when an invite token is present */}
          <Route path="/call/invite/:token" element={<LiveCallInviteRoom />} />
          <Route path="/call/:bookingId" element={<LiveCallRoom />} />
          <Route path="/room/:roomId" element={<LiveRoomPage />} />
          <Route path="/create-lesson" element={<PrivateRoute><CreateLesson /></PrivateRoute>} />
          <Route path="/edit-lesson/:lessonId" element={<PrivateRoute><CreateLesson /></PrivateRoute>} />

          {/* Admin routes */}
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route path="users" element={<AdminUsers />} />
            <Route path="lessons" element={<AdminLessons />} />
            <Route path="chats" element={<AdminChats />} />
            <Route path="metrics" element={<AdminMetrics />} />
            <Route path="drill-library" element={<AdminClipLibrary />} />
            <Route path="pro-library" element={<AdminProLibrary />} />
            <Route path="invitations" element={<AdminInvitations />} />
            <Route path="billing" element={<AdminBilling />} />
            <Route path="reconciliation" element={<AdminReconciliation />} />
            <Route path="live-lessons" element={<AdminLiveLessons />} />
            <Route path="account" element={<AdminAccount />} />
            <Route path="action-log" element={<RootRoute><AdminActionLog /></RootRoute>} />
            <Route path="support" element={<AdminSupport />} />
            <Route path="staff" element={<RootRoute><AdminStaff /></RootRoute>} />
          </Route>

          {/* Video Editor Test - using AdminLayout */}
          <Route path="/video-editor-test" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<VideoEditorTest />} />
          </Route>

          {/* Video Draft Test - using AdminLayout */}
          <Route path="/video-draft-test" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<VideoDraftTest />} />
          </Route>
              </Routes>
              </Suspense>
              </PendingUploadsProvider>
              </NotificationsProvider>
              </LessonProvider>
            </AuthProvider>
          </Router>
        </ResponsiveProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
